/**
 * Google Analytics Utility Functions
 * Helper functions to track custom events and page views
 */

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: any
    ) => void;
    dataLayer: any[];
  }
}

/**
 * Track custom events in Google Analytics
 * @param eventName - Name of the event (e.g., 'button_click', 'form_submit')
 * @param eventParams - Additional parameters for the event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
    console.log('Analytics Event:', eventName, eventParams);
  }
};

/**
 * Track page views
 * @param pagePath - The path of the page being viewed
 * @param pageTitle - The title of the page
 */
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-BVNLY1C4C2', {
      page_path: pagePath,
      page_title: pageTitle,
    });
    console.log('Analytics Page View:', pagePath, pageTitle);
  }
};

/**
 * Track user interactions
 */
export const analyticsEvents = {
  // User Authentication Events
  signUp: (method: string) => {
    trackEvent('sign_up', { method });
  },
  
  login: (method: string) => {
    trackEvent('login', { method });
  },
  
  logout: () => {
    trackEvent('logout');
  },

  // Profile Events
  profileView: (designerId: string) => {
    trackEvent('view_profile', { designer_id: designerId });
  },
  
  profileComplete: () => {
    trackEvent('complete_profile');
  },
  
  profileEdit: () => {
    trackEvent('edit_profile');
  },

  // Designer Search Events
  searchDesigners: (searchTerm: string, filters?: any) => {
    trackEvent('search', {
      search_term: searchTerm,
      ...filters,
    });
  },

  // Contact Events
  contactRequest: (designerId: string, isPremium: boolean) => {
    trackEvent('contact_request', {
      designer_id: designerId,
      is_premium: isPremium,
    });
  },

  // Job Events
  jobPost: (jobType: string) => {
    trackEvent('post_job', { job_type: jobType });
  },
  
  jobApplication: (jobId: string) => {
    trackEvent('apply_job', { job_id: jobId });
  },

  // Rating Events
  submitRating: (rating: number, designerId: string) => {
    trackEvent('submit_rating', {
      rating_value: rating,
      designer_id: designerId,
    });
  },

  // Blog Events
  viewBlog: (blogId: string, blogTitle: string) => {
    trackEvent('view_blog', {
      blog_id: blogId,
      blog_title: blogTitle,
    });
  },

  // Pricing Events
  viewPricing: () => {
    trackEvent('view_pricing');
  },
  
  upgradeToPremium: (plan: string) => {
    trackEvent('upgrade_premium', { plan });
  },

  // Feedback Events
  submitFeedback: (feedbackType: string) => {
    trackEvent('submit_feedback', { feedback_type: feedbackType });
  },

  // Social Share Events
  shareProfile: (method: string) => {
    trackEvent('share', {
      method,
      content_type: 'profile',
    });
  },

  // Custom Button Clicks
  buttonClick: (buttonName: string, location: string) => {
    trackEvent('button_click', {
      button_name: buttonName,
      location,
    });
  },
};

const analytics = {
  trackEvent,
  trackPageView,
  analyticsEvents,
};

export default analytics;
